import { useEffect, useState } from 'react'
import type { CoverageLookups, CoverageViewRow } from './coverageReport'
import { getCoverageLookups, getCoverageRows } from './coverageReportStore'

const EMPTY_LOOKUPS: CoverageLookups = { teams: new Map(), shifts: new Map(), assignments: new Map(), supervisors: new Map() }

/**
 * The coverage view for a date range, with the names its ids stand for. Every
 * report built on the coverage calculation loads its data through this.
 */
export function useCoverageData(from: string, to: string) {
  const [rows, setRows] = useState<CoverageViewRow[]>([])
  const [lookups, setLookups] = useState<CoverageLookups>(EMPTY_LOOKUPS)
  const [loading, setLoading] = useState(true)
  const [lookupsFailed, setLookupsFailed] = useState(false)
  const [rowsFailed, setRowsFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    getCoverageLookups()
      .then((loaded) => {
        if (!cancelled) setLookups(loaded)
      })
      .catch(() => {
        if (!cancelled) setLookupsFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!from || !to || to < from) return
    let cancelled = false
    // Marks the new range as loading before it is fetched; the pattern the
    // other stores' pages use for a refetch on a changed input.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    getCoverageRows(from, to)
      .then((loaded) => {
        if (cancelled) return
        setRows(loaded)
        setRowsFailed(false)
      })
      .catch(() => {
        if (!cancelled) setRowsFailed(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [from, to])

  return { rows, lookups, loading, failed: lookupsFailed || rowsFailed }
}
